import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import Table from '@/shared/components/Table/Table'
import DatePicker, {
  DateRangeValue
} from '@/shared/components/DatePicker/DatePicker'
import Select from '@/shared/components/Select/Select'
import Button from '@/shared/components/Button/Button'
import { OptionType } from '@/shared/api/BaseAPI.model'
import { TABLE_KEYS } from '@/shared/components/Table/models/Filter.model'
import {
  SalesReportsAPI,
  useMenuItemsSalesTable,
  useSalesReportsColumnsConfig
} from '@/features/sales-reports'
import styles from '@/features/sales-reports/SalesReports.module.scss'

type ItemFilter = 'dish' | 'topping' | null

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

function MenuItemsSalesTablePage() {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language || 'en'

  const [dateRange, setDateRange] = useState<DateRangeValue>(() => {
    const end = dayjs()
    const start = end.subtract(7, 'day')
    return [start, end]
  })

  const itemFilterOptions = useMemo<OptionType[]>(
    () => [
      { label: t('SALES_REPORTS.FILTER.ITEM_TYPE.ALL'), value: '' },
      { label: t('SALES_REPORTS.FILTER.ITEM_TYPE.DISH'), value: 'dish' },
      { label: t('SALES_REPORTS.FILTER.ITEM_TYPE.TOPPING'), value: 'topping' }
    ],
    [t]
  )

  const [itemFilter, setItemFilter] = useState<OptionType>(itemFilterOptions[0])

  const [startDayjs, endDayjs] = dateRange
  const startDate = startDayjs?.format('YYYY-MM-DD') ?? ''
  const endDate = endDayjs?.format('YYYY-MM-DD') ?? ''

  const apiItemFilter = (itemFilter.value as ItemFilter) || null

  const { data: menuItemsSalesTableByLanguage } = useMenuItemsSalesTable({
    startDate,
    endDate,
    limit: 100,
    itemFilter: apiItemFilter ?? undefined
  })

  const columns = useSalesReportsColumnsConfig()
  const columnsModel = ['number', 'name', 'price', 'quantity', 'percentage']

  const rows = useMemo(() => {
    if (!menuItemsSalesTableByLanguage?.items) return []
    return menuItemsSalesTableByLanguage.items.map((it, idx) => ({
      id: it.itemId || idx,
      number: idx + 1,
      name:
        (it.translations && it.translations[currentLanguage]) ||
        Object.values(it.translations ?? {})[0] ||
        '',
      price: it.price,
      quantity: it.quantity,
      percentage: it.percentage
    }))
  }, [menuItemsSalesTableByLanguage, currentLanguage])

  const [isExportingPdf, setIsExportingPdf] = useState(false)
  const [isExportingExcel, setIsExportingExcel] = useState(false)

  const canExport = Boolean(startDate && endDate)

  const handleExportPdf = async () => {
    if (!canExport) return
    setIsExportingPdf(true)
    try {
      const blob = await SalesReportsAPI.getDishesSalesPdf({
        language: currentLanguage,
        startDate,
        endDate,
        limit: 100,
        itemFilter: apiItemFilter ?? undefined
      })
      downloadBlob(blob, `menu-items-sales-${startDate}-${endDate}.pdf`)
    } finally {
      setIsExportingPdf(false)
    }
  }

  const handleExportExcel = async () => {
    if (!canExport) return
    setIsExportingExcel(true)
    try {
      const blob = await SalesReportsAPI.getMenuItemsSalesExcel({
        language: currentLanguage,
        startDate,
        endDate,
        limit: 100,
        itemFilter: apiItemFilter ?? undefined
      })
      downloadBlob(blob, `menu-items-sales-${startDate}-${endDate}.xlsx`)
    } finally {
      setIsExportingExcel(false)
    }
  }

  return (
    <div className={styles.subpageWrapper}>
      <div className={styles.controlsRow}>
        <div id="tour-reports-filters" className={styles.filtersGroup}>
          <DatePicker
            label={t('COMMON.DATE_RANGE')}
            value={dateRange}
            onChange={setDateRange}
          />
          <Select
            label={t('SALES_REPORTS.FILTER.ITEM_TYPE')}
            options={itemFilterOptions}
            value={itemFilter}
            onChange={value => {
              if (value && !Array.isArray(value)) {
                setItemFilter(value as OptionType)
              } else if (!value) {
                setItemFilter(itemFilterOptions[0])
              }
            }}
            hideClearIcon
          />
        </div>
        <div id="tour-reports-export" className={styles.exportGroup}>
          <Button
            label={t('SALES_REPORTS.EXPORT.PDF')}
            variant="text"
            color="primary"
            icon="las la-file-pdf"
            onClick={handleExportPdf}
            disabled={!canExport || isExportingPdf}
          />
          <Button
            label={t('SALES_REPORTS.EXPORT.EXCEL')}
            variant="text"
            color="primary"
            icon="las la-file-excel"
            onClick={handleExportExcel}
            disabled={!canExport || isExportingExcel}
          />
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <Table
          key={`menu-items-${currentLanguage}`}
          initialRows={rows}
          columns={columns}
          columnsModel={columnsModel}
          tableKey={TABLE_KEYS.DISHES_SALSES_REPORT}
          filterModel={{ items: [] }}
          hideToolbar
          hideCheckboxSelection
          isLoading={!menuItemsSalesTableByLanguage && canExport}
        />
      </div>
    </div>
  )
}

export default MenuItemsSalesTablePage
